export const DEFAULT_WINDOW_SNAP_THRESHOLD = 12;
export const DEFAULT_WINDOW_SNAP_RELEASE_THRESHOLD = 18;

const EPSILON = 0.001;
const EDGE_DEFINITIONS = {
  x: {
    start: "left",
    end: "right",
    perpendicularStart: "top",
    perpendicularEnd: "bottom"
  },
  y: {
    start: "top",
    end: "bottom",
    perpendicularStart: "left",
    perpendicularEnd: "right"
  }
};

/**
 * Applies magnetic window snapping without reading or mutating the DOM.
 *
 * All rectangles and thresholds use workspace-local CSS pixels.
 */
export function snapWindowGeometry({
  proposedRect,
  mode = "move",
  workspaceBounds,
  otherRects = [],
  currentSnapState = null,
  snapThreshold = DEFAULT_WINDOW_SNAP_THRESHOLD,
  releaseThreshold = DEFAULT_WINDOW_SNAP_RELEASE_THRESHOLD,
  perpendicularThreshold = snapThreshold,
  constraints = {},
  disabled = false
}) {
  const workspace = normalizeWorkspaceBounds(workspaceBounds);
  const normalizedConstraints = normalizeConstraints(constraints, workspace);
  const rect = constrainRect(
    normalizeRect(proposedRect),
    workspace,
    normalizedConstraints
  );
  const emptyResult = () => ({
    rect,
    activeTargets: {
      horizontal: null,
      vertical: null
    },
    snapState: {
      x: null,
      y: null
    },
    guides: []
  });

  if (
    disabled
    || workspace.width <= 0
    || workspace.height <= 0
    || snapThreshold < 0
    || releaseThreshold <= 0
  ) {
    return emptyResult();
  }

  const interaction = normalizeInteractionMode(mode);
  const candidates = [
    ...workspaceCandidates(rect, interaction, workspace),
    ...windowCandidates(
      rect,
      interaction,
      otherRects,
      Math.max(0, perpendicularThreshold)
    )
  ].filter(candidate =>
    isCandidateApplicable(
      rect,
      candidate,
      interaction,
      workspace,
      normalizedConstraints
    )
  );

  const xTarget = selectCandidate(
    candidates.filter(candidate => candidate.axis === "x"),
    currentSnapState?.x,
    Math.max(0, snapThreshold),
    Math.max(releaseThreshold, snapThreshold)
  );
  const yTarget = selectCandidate(
    candidates.filter(candidate => candidate.axis === "y"),
    currentSnapState?.y,
    Math.max(0, snapThreshold),
    Math.max(releaseThreshold, snapThreshold)
  );

  let corrected = rect;
  if (xTarget) corrected = applyCandidate(corrected, xTarget, interaction);
  if (yTarget) corrected = applyCandidate(corrected, yTarget, interaction);
  corrected = constrainRect(corrected, workspace, normalizedConstraints);

  const verticalTarget = publicTarget(xTarget);
  const horizontalTarget = publicTarget(yTarget);
  const guides = [
    xTarget ? guideForCandidate(xTarget, corrected, workspace) : null,
    yTarget ? guideForCandidate(yTarget, corrected, workspace) : null
  ].filter(Boolean);

  return {
    rect: corrected,
    activeTargets: {
      horizontal: horizontalTarget,
      vertical: verticalTarget
    },
    snapState: {
      x: verticalTarget,
      y: horizontalTarget
    },
    guides
  };
}

function workspaceCandidates(rect, interaction, workspace) {
  const candidates = [];
  for (const axis of ["x", "y"]) {
    const definition = EDGE_DEFINITIONS[axis];
    const sourceEdges = sourceEdgesForAxis(interaction, axis);
    const workspaceStart = axis === "x" ? workspace.x : workspace.y;
    const workspaceEnd = workspaceStart + (
      axis === "x" ? workspace.width : workspace.height
    );
    if (sourceEdges.includes(definition.start)) {
      candidates.push(createCandidate({
        axis,
        sourceEdge: definition.start,
        targetEdge: definition.start,
        targetId: "workspace",
        targetKind: "workspace",
        kind: "boundary",
        position: workspaceStart,
        rect,
        targetRect: workspace
      }));
    }
    if (sourceEdges.includes(definition.end)) {
      candidates.push(createCandidate({
        axis,
        sourceEdge: definition.end,
        targetEdge: definition.end,
        targetId: "workspace",
        targetKind: "workspace",
        kind: "boundary",
        position: workspaceEnd,
        rect,
        targetRect: workspace
      }));
    }
  }
  return candidates;
}

function windowCandidates(rect, interaction, otherRects, perpendicularThreshold) {
  const candidates = [];
  const targets = otherRects
    .filter(isEligibleTarget)
    .map((target, index) => ({
      id: String(target.id ?? `window-${index}`),
      rect: normalizeRect(target.rect ?? target)
    }))
    .filter(target => isValidRect(target.rect))
    .sort((left, right) => left.id.localeCompare(right.id));

  for (const target of targets) {
    for (const axis of ["x", "y"]) {
      if (!perpendicularSpansConnect(
        rect,
        target.rect,
        axis,
        perpendicularThreshold
      )) {
        continue;
      }

      const definition = EDGE_DEFINITIONS[axis];
      const sourceEdges = sourceEdgesForAxis(interaction, axis);
      for (const sourceEdge of sourceEdges) {
        const alignmentTargetEdge = sourceEdge;
        candidates.push(createCandidate({
          axis,
          sourceEdge,
          targetEdge: alignmentTargetEdge,
          targetId: target.id,
          targetKind: "window",
          kind: "alignment",
          position: edgePosition(target.rect, alignmentTargetEdge),
          rect,
          targetRect: target.rect
        }));

        const adjacentTargetEdge = sourceEdge === definition.start
          ? definition.end
          : definition.start;
        candidates.push(createCandidate({
          axis,
          sourceEdge,
          targetEdge: adjacentTargetEdge,
          targetId: target.id,
          targetKind: "window",
          kind: "adjacent",
          position: edgePosition(target.rect, adjacentTargetEdge),
          rect,
          targetRect: target.rect
        }));
      }
    }
  }
  return candidates;
}

function createCandidate({
  axis,
  sourceEdge,
  targetEdge,
  targetId,
  targetKind,
  kind,
  position,
  rect,
  targetRect
}) {
  const distance = Math.abs(edgePosition(rect, sourceEdge) - position);
  return {
    axis,
    sourceEdge,
    targetEdge,
    targetId,
    targetKind,
    kind,
    position,
    distance,
    targetRect,
    key: [
      axis,
      sourceEdge,
      targetKind,
      targetId,
      targetEdge,
      kind
    ].join(":")
  };
}

function selectCandidate(candidates, current, snapThreshold, releaseThreshold) {
  if (current?.key) {
    const active = candidates.find(candidate => candidate.key === current.key);
    if (active && active.distance < releaseThreshold) return active;
  }

  return candidates
    .filter(candidate => candidate.distance <= snapThreshold)
    .sort(compareCandidates)[0] ?? null;
}

function compareCandidates(left, right) {
  const distanceDifference = left.distance - right.distance;
  if (Math.abs(distanceDifference) > EPSILON) return distanceDifference;

  const kindDifference = candidateKindRank(left.kind) - candidateKindRank(right.kind);
  if (kindDifference !== 0) return kindDifference;
  return left.key.localeCompare(right.key);
}

function candidateKindRank(kind) {
  if (kind === "adjacent") return 0;
  if (kind === "boundary") return 1;
  return 2;
}

function isCandidateApplicable(rect, candidate, interaction, workspace, constraints) {
  const corrected = applyCandidate(rect, candidate, interaction);
  if (!isValidRect(corrected)) return false;
  if (
    corrected.width < constraints.minWidth - EPSILON
    || corrected.height < constraints.minHeight - EPSILON
    || corrected.width > constraints.maxWidth + EPSILON
    || corrected.height > constraints.maxHeight + EPSILON
  ) {
    return false;
  }
  return (
    corrected.x >= workspace.x - EPSILON
    && corrected.y >= workspace.y - EPSILON
    && corrected.x + corrected.width <= workspace.x + workspace.width + EPSILON
    && corrected.y + corrected.height <= workspace.y + workspace.height + EPSILON
  );
}

function applyCandidate(rect, candidate, interaction) {
  const corrected = { ...rect };
  const isMove = interaction.type === "move";
  if (candidate.axis === "x") {
    if (isMove) {
      corrected.x = candidate.sourceEdge === "left"
        ? candidate.position
        : candidate.position - corrected.width;
    } else if (candidate.sourceEdge === "left") {
      const right = corrected.x + corrected.width;
      corrected.x = candidate.position;
      corrected.width = right - candidate.position;
    } else {
      corrected.width = candidate.position - corrected.x;
    }
  } else if (isMove) {
    corrected.y = candidate.sourceEdge === "top"
      ? candidate.position
      : candidate.position - corrected.height;
  } else if (candidate.sourceEdge === "top") {
    const bottom = corrected.y + corrected.height;
    corrected.y = candidate.position;
    corrected.height = bottom - candidate.position;
  } else {
    corrected.height = candidate.position - corrected.y;
  }
  return corrected;
}

function guideForCandidate(candidate, rect, workspace) {
  let start;
  let end;
  if (candidate.targetKind === "workspace") {
    start = candidate.axis === "x" ? rect.y : rect.x;
    end = start + (candidate.axis === "x" ? rect.height : rect.width);
  } else {
    start = candidate.axis === "x"
      ? candidate.targetRect.y
      : candidate.targetRect.x;
    end = start + (
      candidate.axis === "x"
        ? candidate.targetRect.height
        : candidate.targetRect.width
    );
  }

  const minimum = candidate.axis === "x" ? workspace.y : workspace.x;
  const maximum = minimum + (
    candidate.axis === "x" ? workspace.height : workspace.width
  );
  return {
    axis: candidate.axis,
    position: candidate.position,
    start: clamp(start, minimum, maximum),
    end: clamp(end, minimum, maximum),
    kind: candidate.kind,
    targetId: candidate.targetId,
    sourceEdge: candidate.sourceEdge,
    targetEdge: candidate.targetEdge
  };
}

function publicTarget(candidate) {
  if (!candidate) return null;
  return {
    key: candidate.key,
    axis: candidate.axis,
    sourceEdge: candidate.sourceEdge,
    targetEdge: candidate.targetEdge,
    targetId: candidate.targetId,
    targetKind: candidate.targetKind,
    kind: candidate.kind,
    position: candidate.position,
    distance: candidate.distance
  };
}

function perpendicularSpansConnect(source, target, axis, threshold) {
  const sourceStart = axis === "x" ? source.y : source.x;
  const sourceEnd = sourceStart + (axis === "x" ? source.height : source.width);
  const targetStart = axis === "x" ? target.y : target.x;
  const targetEnd = targetStart + (axis === "x" ? target.height : target.width);
  const gap = Math.max(
    0,
    Math.max(sourceStart, targetStart) - Math.min(sourceEnd, targetEnd)
  );
  return gap <= threshold;
}

function sourceEdgesForAxis(interaction, axis) {
  const definition = EDGE_DEFINITIONS[axis];
  if (interaction.type === "move") return [definition.start, definition.end];
  const edges = [];
  if (interaction.edges.includes(axis === "x" ? "w" : "n")) {
    edges.push(definition.start);
  }
  if (interaction.edges.includes(axis === "x" ? "e" : "s")) {
    edges.push(definition.end);
  }
  return edges;
}

function normalizeInteractionMode(mode) {
  if (mode === "move" || mode?.type === "move") {
    return { type: "move", edges: "" };
  }
  const edges = String(
    typeof mode === "string"
      ? mode.replace(/^resize-?/, "")
      : mode?.edges ?? mode?.edge ?? ""
  ).toLowerCase();
  return {
    type: "resize",
    edges: [...new Set([...edges].filter(edge => "nesw".includes(edge)))].join("")
  };
}

function isEligibleTarget(target) {
  return !(
    target == null
    || target.eligible === false
    || target.visible === false
    || target.hidden === true
    || target.minimized === true
    || target.closing === true
    || target.interactive === false
  );
}

function normalizeWorkspaceBounds(bounds = {}) {
  return {
    x: finite(bounds.x, 0),
    y: finite(bounds.y, 0),
    width: Math.max(0, finite(bounds.width, 0)),
    height: Math.max(0, finite(bounds.height, 0))
  };
}

function normalizeConstraints(constraints, workspace) {
  const minWidth = clamp(finite(constraints.minWidth, 0), 0, workspace.width);
  const minHeight = clamp(finite(constraints.minHeight, 0), 0, workspace.height);
  return {
    minWidth,
    minHeight,
    maxWidth: clamp(
      finite(constraints.maxWidth, workspace.width),
      minWidth,
      workspace.width
    ),
    maxHeight: clamp(
      finite(constraints.maxHeight, workspace.height),
      minHeight,
      workspace.height
    )
  };
}

function normalizeRect(rect = {}) {
  return {
    x: finite(rect.x, 0),
    y: finite(rect.y, 0),
    width: Math.max(0, finite(rect.width, 0)),
    height: Math.max(0, finite(rect.height, 0))
  };
}

function constrainRect(rect, workspace, constraints) {
  const width = clamp(rect.width, constraints.minWidth, constraints.maxWidth);
  const height = clamp(rect.height, constraints.minHeight, constraints.maxHeight);
  return {
    x: clamp(
      rect.x,
      workspace.x,
      Math.max(workspace.x, workspace.x + workspace.width - width)
    ),
    y: clamp(
      rect.y,
      workspace.y,
      Math.max(workspace.y, workspace.y + workspace.height - height)
    ),
    width,
    height
  };
}

function isValidRect(rect) {
  return (
    Number.isFinite(rect.x)
    && Number.isFinite(rect.y)
    && Number.isFinite(rect.width)
    && Number.isFinite(rect.height)
    && rect.width > 0
    && rect.height > 0
  );
}

function edgePosition(rect, edge) {
  if (edge === "left") return rect.x;
  if (edge === "right") return rect.x + rect.width;
  if (edge === "top") return rect.y;
  return rect.y + rect.height;
}

function finite(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}
